// A native EPUB 3 checker modelled on the W3C's epubcheck (`EpubCheck`, SuitePreflight/EpubCheck.swift), with its
// message ids: container layout, package metadata, manifest and spine, navigation, well-formed XHTML, unique ids,
// links that resolve, ARIA and DPUB roles. The real epubcheck (Java) runs only where it's installed.
import { XMLFailure, XMLTree, type XMLNode } from './xml.ts';
import { ZipReader, fromUTF8 } from './zip.ts';

export interface EpubFinding {
  severity: 'error' | 'warning';
  /** epubcheck's message id for the same problem (RSC-005, OPF-030…). */
  code: string;
  message: string;
  /** The file (relative to the package document) and line, when known. */
  file?: string;
  line?: number;
}

const MEDIA_TYPES: Record<string, string[]> = {
  xhtml: ['application/xhtml+xml'], html: ['application/xhtml+xml'], css: ['text/css'],
  jpg: ['image/jpeg'], jpeg: ['image/jpeg'], png: ['image/png'], gif: ['image/gif'], webp: ['image/webp'],
  svg: ['image/svg+xml'], ncx: ['application/x-dtbncx+xml'], js: ['application/javascript', 'text/javascript'],
  otf: ['font/otf', 'application/font-sfnt', 'application/vnd.ms-opentype'],
  ttf: ['font/ttf', 'application/font-sfnt'], woff: ['font/woff', 'application/font-woff'], woff2: ['font/woff2'],
  mp3: ['audio/mpeg'], mp4: ['audio/mp4', 'video/mp4'], smil: ['application/smil+xml'],
};

const ARIA_ROLES = new Set([
  'alert', 'alertdialog', 'application', 'article', 'banner', 'blockquote', 'button', 'caption', 'cell', 'checkbox', 'code',
  'columnheader', 'combobox', 'complementary', 'contentinfo', 'definition', 'deletion', 'dialog', 'directory', 'document',
  'emphasis', 'feed', 'figure', 'form', 'generic', 'grid', 'gridcell', 'group', 'heading', 'img', 'insertion', 'link', 'list',
  'listbox', 'listitem', 'log', 'main', 'marquee', 'math', 'menu', 'menubar', 'menuitem', 'menuitemcheckbox', 'menuitemradio',
  'meter', 'navigation', 'none', 'note', 'option', 'paragraph', 'presentation', 'progressbar', 'radio', 'radiogroup', 'region',
  'row', 'rowgroup', 'rowheader', 'scrollbar', 'search', 'searchbox', 'separator', 'slider', 'spinbutton', 'status', 'strong',
  'subscript', 'superscript', 'switch', 'tab', 'table', 'tablist', 'tabpanel', 'term', 'textbox', 'time', 'timer', 'toolbar',
  'tooltip', 'tree', 'treegrid', 'treeitem',
]);
/** DPUB-ARIA 1.1 (doc-biblioentry and doc-endnote are deprecated, and epubcheck rejects them). */
const DPUB_ROLES = new Set([
  'doc-abstract', 'doc-acknowledgments', 'doc-afterword', 'doc-appendix', 'doc-backlink', 'doc-bibliography', 'doc-biblioref',
  'doc-chapter', 'doc-colophon', 'doc-conclusion', 'doc-cover', 'doc-credit', 'doc-credits', 'doc-dedication', 'doc-endnotes',
  'doc-epigraph', 'doc-epilogue', 'doc-errata', 'doc-example', 'doc-footnote', 'doc-foreword', 'doc-glossary', 'doc-glossref',
  'doc-index', 'doc-introduction', 'doc-noteref', 'doc-notice', 'doc-pagebreak', 'doc-pagefooter', 'doc-pageheader',
  'doc-pagelist', 'doc-part', 'doc-preface', 'doc-prologue', 'doc-pullquote', 'doc-qna', 'doc-subtitle', 'doc-tip', 'doc-toc',
]);
/** The roles HTML allows on the sectioning elements the writer uses (as epubcheck's schema has them). */
const ROLES_BY_ELEMENT: Record<string, Set<string>> = {
  section: new Set(['alert', 'alertdialog', 'application', 'banner', 'complementary', 'contentinfo', 'dialog', 'doc-abstract',
    'doc-acknowledgments', 'doc-afterword', 'doc-appendix', 'doc-bibliography', 'doc-chapter', 'doc-colophon',
    'doc-conclusion', 'doc-credit', 'doc-credits', 'doc-dedication', 'doc-endnotes', 'doc-epigraph', 'doc-epilogue',
    'doc-errata', 'doc-example', 'doc-foreword', 'doc-glossary', 'doc-index', 'doc-introduction', 'doc-notice',
    'doc-pagelist', 'doc-part', 'doc-preface', 'doc-prologue', 'doc-pullquote', 'doc-qna', 'doc-toc', 'document', 'feed',
    'group', 'log', 'main', 'marquee', 'navigation', 'none', 'note', 'presentation', 'region', 'search', 'status', 'tabpanel']),
  nav: new Set(['doc-index', 'doc-pagelist', 'doc-toc', 'menu', 'menubar', 'navigation', 'none', 'presentation', 'tablist']),
  aside: new Set(['feed', 'none', 'note', 'presentation', 'region', 'search', 'doc-dedication', 'doc-example', 'doc-footnote',
    'doc-glossary', 'doc-pullquote', 'doc-tip']),
};

export function roleProblem(role: string, element: string): string | null {
  if (!ARIA_ROLES.has(role) && !DPUB_ROLES.has(role)) return `“${role}” isn't an ARIA role.`;
  const allowed = ROLES_BY_ELEMENT[element];
  if (allowed && !allowed.has(role)) return `The role “${role}” isn't allowed on <${element}>.`;
  return null;
}

const trimAll = (s: string) => s.replace(/^[\s\u0085]+|[\s\u0085]+$/gu, '');
const dirname = (p: string) => (p.includes('/') ? p.slice(0, p.lastIndexOf('/')) : '');
const extension = (p: string) => { const name = p.slice(p.lastIndexOf('/') + 1); const dot = name.lastIndexOf('.'); return dot > 0 ? name.slice(dot + 1) : ''; };
const percentDecoded = (s: string) => { try { return decodeURIComponent(s); } catch { return s; } };

/** `EpubCheck.check`. */
export function epubCheck(data: Uint8Array): EpubFinding[] {
  const f: EpubFinding[] = [];
  const error = (code: string, message: string, file?: string, line?: number) =>
    f.push({ severity: 'error', code, message, ...(file !== undefined ? { file } : {}), ...(line !== undefined ? { line } : {}) });
  const warning = (code: string, message: string, file?: string, line?: number) =>
    f.push({ severity: 'warning', code, message, ...(file !== undefined ? { file } : {}), ...(line !== undefined ? { line } : {}) });

  // MARK: Container (OCF)
  let zip: ZipReader;
  try { zip = new ZipReader(data); } catch {
    return [{ severity: 'error', code: 'PKG-004', message: "The file isn't a readable ZIP archive." }];
  }
  const bytes = data.subarray(0, 64);
  if (bytes.length >= 38 && fromUTF8(bytes.subarray(30, 38)) === 'mimetype') {
    if ((bytes[8] | (bytes[9] << 8)) !== 0) error('PKG-005', 'The mimetype file must be stored uncompressed.');
    if ((bytes[28] | (bytes[29] << 8)) !== 0) error('PKG-005', 'The mimetype file must have no extra field.');
  } else {
    error('PKG-006', 'The mimetype file must be the first file in the archive.');
  }
  const mimetype = zip.data('mimetype');
  if (!mimetype || fromUTF8(mimetype) !== 'application/epub+zip') error('PKG-007', 'The mimetype file must contain exactly “application/epub+zip”.');
  const containerData = zip.data('META-INF/container.xml');
  if (!containerData) {
    error('RSC-002', 'META-INF/container.xml is missing.');
    return f;
  }
  let rootfile: string | undefined;
  try { rootfile = new XMLTree(containerData).root.descendants('rootfile')[0]?.attributes['full-path']; } catch { rootfile = undefined; }
  if (rootfile === undefined) {
    error('RSC-005', "META-INF/container.xml doesn't name the package document.", 'META-INF/container.xml');
    return f;
  }
  const opfData = zip.data(rootfile);
  if (!opfData) {
    error('OPF-002', `The package document ${rootfile} is missing.`);
    return f;
  }
  const base = dirname(rootfile);
  const resolve = (href: string, from: string) => {
    const dir = dirname(from);
    const joined = dir === '' ? href : dir + '/' + href;
    const parts: string[] = [];
    for (const part of joined.split('/').filter((p) => p !== '')) {
      if (part === '..') parts.pop(); else if (part !== '.') parts.push(part);
    }
    return percentDecoded(parts.join('/'));
  };
  const shown = (path: string) => (base === '' || !path.startsWith(base + '/') ? path : path.slice(base.length + 1));

  // MARK: Package document
  let opf: XMLTree;
  try { opf = new XMLTree(opfData); } catch (e) {
    error('RSC-016', `The package document isn't well-formed XML: ${(e as Error).message}`, shown(rootfile), e instanceof XMLFailure ? e.line : undefined);
    return f;
  }
  const pkg = opf.root;
  const opfName = shown(rootfile);
  if (pkg.name !== 'package') error('RSC-005', "The package document's root element must be <package>.", opfName);
  if (pkg.attributes.version !== '3.0') warning('PKG-001', `The package isn't EPUB 3.0 (version ${pkg.attributes.version ?? 'missing'}).`, opfName);
  const metadata = pkg.children.find((c) => c.name === 'metadata');
  const identifiers = metadata?.children.filter((c) => c.name === 'dc:identifier') ?? [];
  if (identifiers.every((i) => trimAll(i.text) === '')) error('RSC-005', 'The package has no identifier (dc:identifier).', opfName);
  const unique = pkg.attributes['unique-identifier'];
  if (unique !== undefined) {
    if (!identifiers.some((i) => i.attributes.id === unique)) error('OPF-030', `The unique-identifier “${unique}” doesn't match a dc:identifier.`, opfName);
  } else {
    error('RSC-005', 'The package element has no unique-identifier.', opfName);
  }
  for (const [tag, label] of [['dc:title', 'title'], ['dc:language', 'language']]) {
    if ((metadata?.children.filter((c) => c.name === tag) ?? []).every((c) => trimAll(c.text) === '')) error('RSC-005', `The package has no ${label} (${tag}).`, opfName);
  }
  const modified = metadata?.children.filter((c) => c.name === 'meta' && c.attributes.property === 'dcterms:modified') ?? [];
  if (modified.length !== 1) {
    error('RSC-005', modified.length === 0 ? 'The package has no modification date (dcterms:modified).' : 'The package has more than one modification date.', opfName);
  } else if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(modified[0].text.replace(/^[ \t]+|[ \t]+$/g, ''))) {
    error('RSC-005', 'The modification date must look like 2026-09-27T09:30:00Z.', opfName, modified[0].line);
  }
  if (!(metadata?.children.some((c) => c.name === 'meta' && c.attributes.property === 'schema:accessibilitySummary') ?? false)) {
    warning('ACC-003', 'Accessibility metadata is missing (schema:accessibilitySummary and related).', opfName);
  }

  // Manifest
  interface Item { id: string; path: string; href: string; mediaType: string; properties: string[] }
  const items = new Map<string, Item>(), byPath = new Map<string, Item>();
  const manifestItems = pkg.children.find((c) => c.name === 'manifest')?.children.filter((c) => c.name === 'item') ?? [];
  const knownProperties = new Set(['cover-image', 'mathml', 'nav', 'remote-resources', 'scripted', 'svg', 'switch']);
  for (const node of manifestItems) {
    const id = node.attributes.id, href = node.attributes.href, type = node.attributes['media-type'];
    if (id === undefined || href === undefined || type === undefined) {
      error('RSC-005', 'A manifest item lacks an id, href or media-type.', opfName, node.line);
      continue;
    }
    if (items.has(id)) error('RSC-005', `The id “${id}” is used by more than one manifest item.`, opfName, node.line);
    const isRemote = href.includes('://');
    const path = isRemote ? href : resolve(href, rootfile);
    if (byPath.has(path)) error('OPF-074', `${href} is listed in the manifest twice.`, opfName, node.line);
    const properties = (node.attributes.properties ?? '').split(' ').filter((p) => p !== '');
    for (const property of properties) if (!knownProperties.has(property)) error('OPF-027', `Unknown manifest property “${property}” on ${href}.`, opfName, node.line);
    const item: Item = { id, path, href, mediaType: type, properties };
    items.set(id, item);
    byPath.set(path, item);
    if (!isRemote) {
      if (!zip.entries.has(path)) error('RSC-001', `${href} is in the manifest but not in the EPUB.`, opfName, node.line);
      const expected = MEDIA_TYPES[extension(path).toLowerCase()];
      if (expected && !expected.includes(type)) warning('OPF-029', `${href} is declared as ${type} but looks like ${expected[0]}.`, opfName, node.line);
    }
  }
  const navs = [...items.values()].filter((i) => i.properties.includes('nav'));
  if (navs.length !== 1) error('RSC-005', navs.length === 0 ? 'There is no navigation document (a manifest item with properties="nav").' : 'There is more than one navigation document.', opfName);
  if ([...items.values()].filter((i) => i.properties.includes('cover-image')).length > 1) error('RSC-005', 'More than one item is marked as the cover image.', opfName);

  // Spine
  const spine = pkg.children.find((c) => c.name === 'spine');
  const refs = spine?.children.filter((c) => c.name === 'itemref') ?? [];
  if (!refs.length) error('RSC-005', 'The spine is empty.', opfName);
  const seenRefs = new Set<string>();
  for (const ref of refs) {
    const idref = ref.attributes.idref;
    if (idref === undefined) continue;
    if (seenRefs.has(idref)) error('OPF-034', `The spine lists “${idref}” twice.`, opfName, ref.line);
    seenRefs.add(idref);
    const item = items.get(idref);
    if (!item) {
      error('OPF-049', `The spine refers to “${idref}”, which isn't in the manifest.`, opfName, ref.line);
      continue;
    }
    if (!['application/xhtml+xml', 'image/svg+xml'].includes(item.mediaType)) error('OPF-043', `The spine item ${item.href} isn't a content document.`, opfName, ref.line);
    const linear = ref.attributes.linear;
    if (linear !== undefined && linear !== 'yes' && linear !== 'no') error('RSC-005', 'linear must be “yes” or “no”.', opfName, ref.line);
  }

  // MARK: Content documents
  const trees = new Map<string, XMLTree>();
  for (const item of items.values()) {
    if (item.mediaType !== 'application/xhtml+xml') continue;
    const content = zip.data(item.path);
    if (!content) continue;
    try { trees.set(item.path, new XMLTree(content)); } catch (e) {
      error('RSC-016', `${item.href} isn't well-formed XHTML: ${(e as Error).message}`, shown(item.path), e instanceof XMLFailure ? e.line : undefined);
    }
  }
  const idsByFile = new Map([...trees].map(([path, tree]) => [path, new Set(tree.root.descendants().flatMap((n) => (n.attributes.id !== undefined ? [n.attributes.id] : [])))]));
  const sorted = [...trees].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  for (const [path, tree] of sorted) {
    const file = shown(path);
    const html = tree.root;
    if (html.name !== 'html' || html.attributes.xmlns !== 'http://www.w3.org/1999/xhtml') error('RSC-005', 'The root element must be <html> in the XHTML namespace.', file);
    const lang = html.attributes.lang, xmlLang = html.attributes['xml:lang'];
    if (lang !== undefined && xmlLang !== undefined && lang !== xmlLang) error('RSC-005', 'lang and xml:lang must have the same value.', file, html.line);
    if (xmlLang === undefined && lang === undefined) warning('ACC-005', "The document doesn't declare its language.", file);
    if (trimAll(html.descendants('title')[0]?.text ?? '') === '') warning('RSC-017', 'The document has no title.', file);
    const seen = new Set<string>();
    for (const node of html.descendants()) {
      const id = node.attributes.id;
      if (id !== undefined) {
        if (seen.has(id)) error('RSC-005', `Duplicate id “${id}”.`, file, node.line);
        seen.add(id);
      }
      const role = node.attributes.role;
      if (role !== undefined) {
        for (const value of role.split(' ').filter((v) => v !== '')) {
          const problem = roleProblem(value, node.name);
          if (problem) error('RSC-005', problem, file, node.line);
        }
      }
      if (node.name === 'img' && node.attributes.alt === undefined) error('RSC-005', 'An image has no alt text.', file, node.line);
      // Links must reach a file in the EPUB (and a fragment in it); resources must be in the manifest.
      for (const attribute of ['href', 'src']) {
        const target = node.attributes[attribute];
        if (!target || target.startsWith('mailto:') || target.startsWith('tel:') || target.startsWith('data:')) continue;
        const isLink = node.name === 'a' || node.name === 'area';
        if (target.includes('://')) {
          if (!isLink && !(byPath.get(path)?.properties.includes('remote-resources') ?? false)) {
            error('RSC-006', `${target} is a remote resource the package doesn't declare.`, file, node.line);
          }
          continue;
        }
        const hash = target.indexOf('#');
        const parts = hash < 0 ? [target] : [target.slice(0, hash), target.slice(hash + 1)];
        const targetPath = parts[0] === '' ? path : resolve(parts[0], path);
        if (!zip.entries.has(targetPath)) {
          error('RSC-007', `The link to ${target} points at a file that isn't in the EPUB.`, file, node.line);
          continue;
        }
        if (!byPath.has(targetPath)) error('RSC-008', `${parts[0]} is used but isn't in the manifest.`, file, node.line);
        const ids = idsByFile.get(targetPath);
        if (parts.length === 2 && parts[1] !== '' && ids && !ids.has(percentDecoded(parts[1]))) {
          error('RSC-012', `The link to ${target} points at an id that doesn't exist.`, file, node.line);
        }
      }
    }
  }
  // Nav links must have text.
  for (const [path, tree] of sorted) {
    for (const nav of tree.root.descendants('nav')) {
      for (const link of nav.descendants('a')) {
        if (trimAll(link.allText) === '' && !link.descendants('img').some((img: XMLNode) => (img.attributes.alt ?? '') !== '')) {
          error('RSC-005', `A link in the navigation${link.attributes.href !== undefined ? ` to ${link.attributes.href}` : ''} has no text.`, shown(path), link.line);
        }
      }
    }
  }
  // The navigation document needs a table of contents.
  const nav = navs[0];
  const navTree = nav ? trees.get(nav.path) : undefined;
  if (nav && navTree) {
    const toc = navTree.root.descendants('nav').find((n) => (n.attributes['epub:type'] ?? '').split(' ').includes('toc'));
    if (!toc) error('RSC-005', 'The navigation document has no table of contents (<nav epub:type="toc">).', shown(nav.path));
    else if (!toc.children.some((c) => c.name === 'ol')) error('RSC-005', 'The table of contents needs an <ol> list.', shown(nav.path), toc.line);
  }
  return f;
}
